/**
 * filters.ts [T246-FOUNDATION]
 *
 * Canonical FilterState type + helpers for transaction filter UI.
 *
 * Extracted from `frontend/src/components/transactions/UnifiedFilterBar.tsx`
 * so that consumers (UnifiedTopBar, TransactionInsightsCard, TagExplorerCard,
 * ExportDialog, useFilterUrlSync, Transactions page) can import the type and
 * its helpers without creating a circular import on the bar component itself.
 *
 * Public surface (stable):
 *   • `FilterState`           — filter value shape (period + every advanced field)
 *   • `FilterPreset`          — saved-preset shape (id + name + filters)
 *   • `getDateRangeFromFilter` — period/dates → ISO dateFrom/dateTo strings
 *   • `filtersToQueryString`  — FilterState → URL-encoded query string
 *   • `filtersFromQueryString`— query string → FilterState | null
 *   • `filtersToApiParams`    — FilterState → Record<string, string> for API calls
 */

import { getStartOfDayInTimezone, getEndOfDayInTimezone } from '@/hooks/useTimezone';

// ─── Public types ──────────────────────────────────────────────────────────────

/**
 * Transaction filter state — drives the period picker, advanced filter sheet,
 * and downstream analytics/summary/insights API calls.
 */
export interface FilterState {
  period: PeriodLiteral;
  dateFrom?: Date;
  dateTo?: Date;
  type?: 'income' | 'expense' | 'transfer';
  accountIds?: string[];
  categoryIds?: string[];
  // [T270-PY] Days of week (1=Sunday .. 7=Saturday — Mongo $dayOfWeek convention).
  // Serialized as the URL key `dow` (CSV: `?dow=1,7`).
  dayOfWeek?: number[];
  amountMin?: number;
  amountMax?: number;
  tags?: string[];
  isRecurring?: boolean;
  status?: 'pending' | 'cleared' | 'reconciled';
  search?: string;
}


/**
 * Saved filter configuration (localStorage-backed in UnifiedFilterBar).
 */
export interface FilterPreset {
  id: string;
  name: string;
  filters: FilterState;
}

/**
 * Default period applied when no other filter state is present.
 * Centralised so URL sync, localStorage hydration, and component defaults
 * all agree on the same baseline.
 */
export const DEFAULT_FILTER_PERIOD: FilterState['period'] = '30d';

// ─── Canonical period contract (R47) ──────────────────────────────────────────

/**
 * All supported period literals — the R47 quick-select presets (1d, 3d, 5d,
 * 1w, 2w, 1m, 3m, 6m, 1y, all), the legacy `7d`/`30d` values still used by persisted
 * URLs and existing callers, and the `custom` marker driven by the calendar.
 */
export type PeriodLiteral =
  | '1d' | '2d' | '3d' | '5d' | '1w' | '2w' | '1m'
  | '3m' | '6m' | '1y'
  | '7d' | '30d'
  | 'all' | 'custom';

/**
 * Canonical preset list rendered by the unified topbar Popover (`1D`, `2D`, `3D`,
 * `5D`, `1W`, `2W`, `1M`, `3M`, `6M`, `1Y`, `All`).
 * Order matches the popover visual order (shortest → longest → all).
 */
export const PERIODS = [
  '1d', '2d', '3d', '5d', '1w', '2w', '1m', '3m', '6m', '1y', 'all',
] as const satisfies readonly PeriodLiteral[];

/**
 * Human-readable labels for every supported period literal. Covers the R47
 * presets plus legacy `7d`/`30d` and the `custom` marker so existing callers
 * (e.g. the local `PERIOD_LABELS` in UnifiedFilterBar) can adopt this canonical
 * map without losing entries.
 */
export const PERIOD_LABELS: Record<PeriodLiteral, string> = {
  '1d': '1D',
  '2d': '2D',
  '3d': '3D',
  '5d': '5D',
  '1w': '1W',
  '2w': '2W',
  '1m': '1M',
  '3m': '3M',
  '6m': '6M',
  '1y': '1Y',
  '7d': '7D',
  '30d': '30D',
  all: 'All',
  custom: 'Custom',
};

// ─── URL-sync param keys (shared between to/from helpers) ─────────────────────

/** Filter-owned param keys — all others (e.g. subscriptionId) are preserved untouched. */
const FILTER_PARAM_KEYS = [
  'period', 'dateFrom', 'dateTo', 'type', 'accounts', 'categories', 'dow',
  'amountMin', 'amountMax', 'tags', 'recurring', 'status', 'q',
] as const;
type FilterParamKey = (typeof FILTER_PARAM_KEYS)[number];

// ─── Date range resolution ────────────────────────────────────────────────────

/**
 * Convert FilterState period/dates → { dateFrom?, dateTo? } ISO strings.
 *
 * [T125-F1-m] When `timezone` is provided, date boundaries are computed
 * relative to the user's timezone midnight instead of browser-local time.
 */
export function getDateRangeFromFilter(
  period: FilterState['period'],
  dateFrom?: Date,
  dateTo?: Date,
  timezone?: string,
): { dateFrom?: string; dateTo?: string } {
  if (period === 'custom') {
    return {
      ...(dateFrom && { dateFrom: dateFrom.toISOString() }),
      ...(dateTo && { dateTo: dateTo.toISOString() }),
    };
  }
  if (period === 'all') return {};
  // R47 presets + legacy `7d`/`30d`. `all` and `custom` short-circuit above,
  // so the Record is exhaustive for every other `PeriodLiteral`.
  const dayMap: Record<Exclude<PeriodLiteral, 'all' | 'custom'>, number> = {
    '1d': 1,
    '2d': 2,
    '3d': 3,
    '5d': 5,
    '1w': 7,
    '2w': 14,
    '1m': 30,
    '7d': 7,
    '30d': 30,
    '3m': 90,
    '6m': 180,
    '1y': 365,
  };
  const days = dayMap[period];

  if (timezone) {
    // Compute boundaries relative to the user's timezone midnight [T125-F1-m]
    const endOfToday = getEndOfDayInTimezone(timezone);
    const startOfDay = getStartOfDayInTimezone(timezone);
    const from = new Date(startOfDay.getTime() - (days - 1) * 24 * 60 * 60 * 1000);
    if (import.meta.env.DEV) {
      console.log('[getDateRangeFromFilter]', {
        period,
        timezone,
        dateFrom: from.toISOString(),
        dateTo: endOfToday.toISOString(),
      });
    }
    return { dateFrom: from.toISOString(), dateTo: endOfToday.toISOString() };
  }

  const now = new Date();
  const from = new Date(now);
  from.setDate(from.getDate() - days);
  return { dateFrom: from.toISOString(), dateTo: now.toISOString() };
}

// ─── URL serialisation ────────────────────────────────────────────────────────

/**
 * Serialize FilterState into a URL query string, omitting the default period.
 * Round-trips with `filtersFromQueryString`.
 */
export function filtersToQueryString(filters: FilterState): string {
  const params = new URLSearchParams();
  if (filters.period && filters.period !== DEFAULT_FILTER_PERIOD) {
    params.set('period', filters.period);
  }
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom.toISOString());
  if (filters.dateTo) params.set('dateTo', filters.dateTo.toISOString());
  if (filters.type) params.set('type', filters.type);
  if (filters.accountIds?.length) params.set('accounts', filters.accountIds.join(','));
  if (filters.categoryIds?.length) params.set('categories', filters.categoryIds.join(','));
  // [T270-PY] Day-of-week drill-through (CSV: ?dow=1,7)
  if (filters.dayOfWeek?.length) params.set('dow', filters.dayOfWeek.join(','));
  if (filters.amountMin != null) params.set('amountMin', String(filters.amountMin));
  if (filters.amountMax != null) params.set('amountMax', String(filters.amountMax));
  if (filters.tags?.length) params.set('tags', filters.tags.join(','));
  if (filters.isRecurring) params.set('recurring', 'true');
  if (filters.status) params.set('status', filters.status);
  if (filters.search) params.set('q', filters.search);
  return params.toString();
}

/**
 * Parse a URL query string (e.g. `?period=30d&type=expense`) into a
 * FilterState. Returns `null` when no filter-owned params are present
 * so callers can distinguish "no filter" from "empty default filter".
 */
export function filtersFromQueryString(queryString: string): FilterState | null {
  const params = new URLSearchParams(queryString);
  return parseUrlFilters(params);
}

// ─── API param construction ───────────────────────────────────────────────────

/**
 * Build an API query-params object (`Record<string, string>`) from a
 * FilterState. Resolves the period/dates into a concrete date range
 * (timezone-aware when `timezone` is provided) and flattens the
 * multi-value `accountIds`/`categoryIds`/`tags` into the single-value
 * fields the analytics endpoints expect.
 *
 * [T86P0] Originally `buildAnalyticsParams` in TransactionInsightsCard.
 * [T125-F1-j] Accepts timezone to pass to API and compute timezone-aware ranges.
 */
export function filtersToApiParams(
  filters: FilterState,
  timezone?: string,
): Record<string, string> {
  const params: Record<string, string> = {};
  const dateRange = getDateRangeFromFilter(
    filters.period,
    filters.dateFrom,
    filters.dateTo,
    timezone,
  );
  if (dateRange.dateFrom) params.dateFrom = dateRange.dateFrom;
  if (dateRange.dateTo) params.dateTo = dateRange.dateTo;
  if (filters.type) params.type = filters.type;
  if (filters.accountIds?.[0]) params.accountId = filters.accountIds[0];
  if (filters.categoryIds?.[0]) params.categoryId = filters.categoryIds[0];
  if (filters.dayOfWeek?.length) params.dow = filters.dayOfWeek.join(',');
  if (filters.search) params.search = filters.search;
  if (filters.amountMin != null) params.amountMin = String(filters.amountMin);
  if (filters.amountMax != null) params.amountMax = String(filters.amountMax);
  if (filters.tags?.length) params.tags = filters.tags.join(',');
  if (filters.isRecurring) params.isRecurring = 'true';
  if (filters.status) params.status = filters.status;
  if (timezone) params.timezone = timezone;
  return params;
}

/** Exposed for callers (e.g. useFilterUrlSync) that need to preserve non-filter params. */
export const FILTER_PARAM_KEYS_LIST: ReadonlyArray<FilterParamKey> = FILTER_PARAM_KEYS;

// ─── Internal helpers ─────────────────────────────────────────────────────────

/** Parse URLSearchParams into a FilterState, or null if no filter params present. */
function parseUrlFilters(params: URLSearchParams): FilterState | null {
  let hasAny = false;

  const period = params.get('period') as FilterState['period'] | null;
  const dateFrom = params.get('dateFrom');
  const dateTo = params.get('dateTo');
  const type = params.get('type') as FilterState['type'] | null;
  const accounts = params.get('accounts');
  const categories = params.get('categories');
  // [T270-PY] Day-of-week drill-through parsing (?dow=1,7)
  const dow = params.get('dow');
  const amountMin = params.get('amountMin');
  const amountMax = params.get('amountMax');
  const tags = params.get('tags');
  const recurring = params.get('recurring');
  const status = params.get('status') as FilterState['status'] | null;
  const q = params.get('q');

  const filters: FilterState = { period: DEFAULT_FILTER_PERIOD };

  // R47 presets + legacy `7d`/`30d` + `custom`. Cast to `readonly string[]` so
  // `URLSearchParams.get('period')` (typed as `string | null`) is accepted by
  // `Array.prototype.includes` without a per-element cast.
  const validPeriods = [...PERIODS, '7d', '30d', 'custom'] as const;
  if (period && (validPeriods as readonly string[]).includes(period)) {
    filters.period = period;
    hasAny = true;
  }
  if (dateFrom) {
    const d = new Date(dateFrom);
    if (!isNaN(d.getTime())) { filters.dateFrom = d; hasAny = true; }
  }
  if (dateTo) {
    const d = new Date(dateTo);
    if (!isNaN(d.getTime())) { filters.dateTo = d; hasAny = true; }
  }
  if (type && ['income', 'expense', 'transfer'].includes(type)) {
    filters.type = type;
    hasAny = true;
  }
  if (accounts) {
    const ids = accounts.split(',').filter(Boolean);
    if (ids.length) { filters.accountIds = ids; hasAny = true; }
  }
  if (categories) {
    const ids = categories.split(',').filter(Boolean);
    if (ids.length) { filters.categoryIds = ids; hasAny = true; }
  }
  // [T270-PY] Parse dow CSV (only valid Mongo $dayOfWeek integers 1..7).
  if (dow) {
    const arr = dow
      .split(',')
      .map(Number)
      .filter((n) => Number.isInteger(n) && n >= 1 && n <= 7);
    if (arr.length) { filters.dayOfWeek = arr; hasAny = true; }
  }
  if (amountMin != null && amountMin !== '') {
    const n = Number(amountMin);
    if (!isNaN(n)) { filters.amountMin = n; hasAny = true; }
  }
  if (amountMax != null && amountMax !== '') {
    const n = Number(amountMax);
    if (!isNaN(n)) { filters.amountMax = n; hasAny = true; }
  }
  if (tags) {
    const arr = tags.split(',').filter(Boolean);
    if (arr.length) { filters.tags = arr; hasAny = true; }
  }
  if (recurring === 'true') { filters.isRecurring = true; hasAny = true; }
  if (status && ['pending', 'cleared', 'reconciled'].includes(status)) {
    filters.status = status;
    hasAny = true;
  }
  if (q) { filters.search = q; hasAny = true; }

  return hasAny ? filters : null;
}
